/**
 * Offline Sync Service
 * Handles IndexedDB storage and background sync with conflict resolution
 */

const DB_NAME = 'FacilityProOffline';
const DB_VERSION = 2;
const STORES = ['work_orders', 'inspections', 'assets', 'sites', 'sync_queue', 'conflicts', 'form_submissions'];

class OfflineSyncService {
  constructor() {
    this.db = null;
    this.isOnline = navigator.onLine;
    this.syncInProgress = false;
    this.listeners = [];
    
    // Listen for online/offline events
    window.addEventListener('online', () => this.handleOnline());
    window.addEventListener('offline', () => this.handleOffline());
  }

  /**
   * Initialize IndexedDB
   */
  async init() {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);
      
      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve(this.db);
      };
      
      request.onupgradeneeded = (event) => {
        const db = event.target.result;
        
        // Create object stores
        STORES.forEach(storeName => {
          if (!db.objectStoreNames.contains(storeName)) {
            const store = db.createObjectStore(storeName, { keyPath: 'id' });
            store.createIndex('updated_at', 'updated_at', { unique: false });
            store.createIndex('synced', 'synced', { unique: false });
          }
        });
      };
    });
  }

  /**
   * Handle coming online - trigger sync
   */
  handleOnline() {
    this.isOnline = true;
    this.notifyListeners({ type: 'online' });
    this.sync();
  }

  /**
   * Handle going offline
   */
  handleOffline() {
    this.isOnline = false;
    this.notifyListeners({ type: 'offline' });
  }

  /**
   * Add listener for sync events
   */
  addListener(callback) {
    this.listeners.push(callback);
    return () => {
      this.listeners = this.listeners.filter(l => l !== callback);
    };
  }

  /**
   * Notify all listeners
   */
  notifyListeners(event) {
    this.listeners.forEach(listener => listener(event));
  }

  /**
   * Save item to local store
   */
  async saveLocal(storeName, item, action = 'update') {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName, 'sync_queue'], 'readwrite');
      const store = transaction.objectStore(storeName);
      const queueStore = transaction.objectStore('sync_queue');
      
      // Mark as not synced
      const localItem = {
        ...item,
        synced: false,
        localUpdatedAt: new Date().toISOString()
      };
      
      store.put(localItem);
      
      // Add to sync queue
      const queueItem = {
        id: `${storeName}_${item.id}_${Date.now()}`,
        entityType: storeName.replace('_', ''),
        entityId: item.id,
        action,
        data: item,
        timestamp: new Date().toISOString(),
        synced: false
      };
      queueStore.put(queueItem);
      
      transaction.oncomplete = () => {
        this.notifyListeners({ 
          type: 'saved', 
          store: storeName, 
          item: localItem 
        });
        resolve(localItem);
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get item from local store
   */
  async getLocal(storeName, id) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.get(id);
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get all items from local store
   */
  async getAllLocal(storeName) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readonly');
      const store = transaction.objectStore(storeName);
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete item from local store
   */
  async deleteLocal(storeName, id) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction([storeName, 'sync_queue'], 'readwrite');
      const store = transaction.objectStore(storeName);
      const queueStore = transaction.objectStore('sync_queue');
      
      store.delete(id);
      
      // Add delete to sync queue
      const queueItem = {
        id: `${storeName}_${id}_delete_${Date.now()}`,
        entityType: storeName.replace('_', ''),
        entityId: id,
        action: 'delete',
        data: { id },
        timestamp: new Date().toISOString(),
        synced: false
      };
      queueStore.put(queueItem);
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get pending sync queue
   */
  async getSyncQueue() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('sync_queue', 'readonly');
      const store = transaction.objectStore('sync_queue');
      // Get all items and filter for unsynced
      const request = store.getAll();
      
      request.onsuccess = () => {
        const results = (request.result || []).filter(item => !item.synced);
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Get conflicts
   */
  async getConflicts() {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('conflicts', 'readonly');
      const store = transaction.objectStore('conflicts');
      const request = store.getAll();
      
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Save conflict for manual resolution
   */
  async saveConflict(conflict) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('conflicts', 'readwrite');
      const store = transaction.objectStore('conflicts');
      
      const conflictItem = {
        ...conflict,
        id: `${conflict.entity_type}_${conflict.id}`,
        createdAt: new Date().toISOString()
      };
      
      store.put(conflictItem);
      
      transaction.oncomplete = () => {
        this.notifyListeners({ type: 'conflict', conflict: conflictItem });
        resolve(conflictItem);
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Resolve conflict
   */
  async resolveConflict(conflictId, resolution, mergedData = null) {
    if (!this.db) await this.init();
    
    const conflict = await this.getLocal('conflicts', conflictId);
    if (!conflict) return null;
    
    // Send resolution to server
    const token = localStorage.getItem('token');
    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sync/resolve-conflicts`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`
      },
      body: JSON.stringify([{
        id: conflict.entityId || conflict.id.split('_')[1],
        entity_type: conflict.entity_type,
        resolution,
        merged_data: mergedData
      }])
    });
    
    if (response.ok) {
      // Remove conflict from local store
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction('conflicts', 'readwrite');
        const store = transaction.objectStore('conflicts');
        store.delete(conflictId);
        
        transaction.oncomplete = () => {
          this.notifyListeners({ type: 'conflict_resolved', conflictId });
          resolve(true);
        };
        transaction.onerror = () => reject(transaction.error);
      });
    }
    
    return false;
  }

  /**
   * Sync with server
   */
  async sync() {
    if (!this.isOnline || this.syncInProgress) return;
    
    this.syncInProgress = true;
    this.notifyListeners({ type: 'sync_started' });
    
    try {
      const queue = await this.getSyncQueue();
      if (queue.length === 0) {
        this.syncInProgress = false;
        this.notifyListeners({ type: 'sync_complete', synced: 0 });
        return;
      }
      
      const token = localStorage.getItem('token');
      const lastSync = localStorage.getItem('lastSyncTimestamp');
      
      // Prepare sync items
      const syncItems = queue.map(item => ({
        id: item.entityId,
        entity_type: item.entityType,
        action: item.action,
        data: item.data,
        client_timestamp: item.timestamp,
        offline_id: item.id
      }));
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sync/push`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          items: syncItems,
          last_sync_timestamp: lastSync
        })
      });
      
      if (!response.ok) throw new Error('Sync failed');
      
      const result = await response.json();
      
      // Mark synced items in queue
      for (const synced of result.synced) {
        if (synced.status === 'success') {
          await this.markQueueItemSynced(synced.offline_id || synced.id);
        }
      }
      
      // Save conflicts for manual resolution
      for (const conflict of result.conflicts) {
        await this.saveConflict(conflict);
      }
      
      // Apply server changes
      for (const change of result.server_changes) {
        const storeName = `${change.entity_type}s`;
        if (STORES.includes(storeName)) {
          await this.updateLocalFromServer(storeName, change.data);
        }
      }
      
      // Save sync timestamp
      localStorage.setItem('lastSyncTimestamp', result.sync_timestamp);
      
      this.notifyListeners({ 
        type: 'sync_complete', 
        synced: result.synced.length,
        conflicts: result.conflicts.length,
        serverChanges: result.server_changes.length
      });
      
    } catch (error) {
      console.error('Sync error:', error);
      this.notifyListeners({ type: 'sync_error', error: error.message });
    } finally {
      this.syncInProgress = false;
    }
  }

  /**
   * Mark queue item as synced
   */
  async markQueueItemSynced(id) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction('sync_queue', 'readwrite');
      const store = transaction.objectStore('sync_queue');
      
      const request = store.get(id);
      request.onsuccess = () => {
        if (request.result) {
          const item = request.result;
          item.synced = true;
          store.put(item);
        }
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Update local data from server
   */
  async updateLocalFromServer(storeName, data) {
    if (!this.db) await this.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.db.transaction(storeName, 'readwrite');
      const store = transaction.objectStore(storeName);
      
      const item = {
        ...data,
        synced: true,
        localUpdatedAt: new Date().toISOString()
      };
      
      store.put(item);
      
      transaction.oncomplete = () => resolve();
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Pull latest data from server
   */
  async pullFromServer(entityTypes = null) {
    if (!this.isOnline) return;
    
    const token = localStorage.getItem('token');
    const lastSync = localStorage.getItem('lastSyncTimestamp');
    
    const params = new URLSearchParams();
    if (lastSync) params.append('since', lastSync);
    if (entityTypes) params.append('entity_types', entityTypes.join(','));
    
    const response = await fetch(
      `${process.env.REACT_APP_BACKEND_URL}/api/sync/pull?${params}`,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    if (!response.ok) throw new Error('Pull failed');
    
    const result = await response.json();
    
    // Update local stores
    for (const change of result.changes) {
      const storeName = `${change.entity_type}s`;
      if (STORES.includes(storeName)) {
        await this.updateLocalFromServer(storeName, change.data);
      }
    }
    
    localStorage.setItem('lastSyncTimestamp', result.sync_timestamp);
    
    return result;
  }

  /**
   * Get sync status
   */
  async getStatus() {
    const queue = await this.getSyncQueue();
    const conflicts = await this.getConflicts();
    
    return {
      isOnline: this.isOnline,
      syncInProgress: this.syncInProgress,
      pendingChanges: queue.filter(q => !q.synced).length,
      conflicts: conflicts.length,
      lastSync: localStorage.getItem('lastSyncTimestamp')
    };
  }

  /**
   * Clear all local data
   */
  async clearAll() {
    if (!this.db) await this.init();
    
    const promises = STORES.map(storeName => {
      return new Promise((resolve, reject) => {
        const transaction = this.db.transaction(storeName, 'readwrite');
        const store = transaction.objectStore(storeName);
        store.clear();
        
        transaction.oncomplete = () => resolve();
        transaction.onerror = () => reject(transaction.error);
      });
    });
    
    await Promise.all(promises);
    localStorage.removeItem('lastSyncTimestamp');
  }
}

// Export singleton instance
export const offlineSync = new OfflineSyncService();
export default offlineSync;

/**
 * Offline Form Submission Service
 * Handles queuing form submissions when offline and syncing when back online
 */
export class OfflineFormService {
  constructor() {
    this.syncService = offlineSync;
  }

  /**
   * Queue a work order creation for offline submission
   */
  async queueWorkOrder(workOrderData) {
    const id = `wo_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const submission = {
      id,
      type: 'work_order',
      action: 'create',
      data: {
        ...workOrderData,
        id,
        status: 'pending',
        created_at: new Date().toISOString(),
        offline_created: true
      },
      timestamp: new Date().toISOString(),
      synced: false,
      retryCount: 0
    };

    await this.saveFormSubmission(submission);
    
    // Also save to local work_orders store for immediate display
    await this.syncService.saveLocal('work_orders', submission.data, 'create');
    
    // Try to sync immediately if online
    if (this.syncService.isOnline) {
      await this.syncFormSubmissions();
    }

    return submission;
  }

  /**
   * Queue a work order status update for offline submission
   */
  async queueWorkOrderUpdate(workOrderId, updateData) {
    const id = `wo_update_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const submission = {
      id,
      type: 'work_order',
      action: 'update',
      entityId: workOrderId,
      data: {
        ...updateData,
        updated_at: new Date().toISOString(),
        offline_updated: true
      },
      timestamp: new Date().toISOString(),
      synced: false,
      retryCount: 0
    };

    await this.saveFormSubmission(submission);
    
    // Update local store
    const existing = await this.syncService.getLocal('work_orders', workOrderId);
    if (existing) {
      await this.syncService.saveLocal('work_orders', { ...existing, ...submission.data }, 'update');
    }
    
    if (this.syncService.isOnline) {
      await this.syncFormSubmissions();
    }

    return submission;
  }

  /**
   * Queue an inspection completion for offline submission
   */
  async queueInspectionComplete(inspectionId, completionData) {
    const id = `insp_complete_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const submission = {
      id,
      type: 'inspection',
      action: 'complete',
      entityId: inspectionId,
      data: {
        ...completionData,
        status: 'completed',
        completed_at: new Date().toISOString(),
        offline_completed: true
      },
      timestamp: new Date().toISOString(),
      synced: false,
      retryCount: 0
    };

    await this.saveFormSubmission(submission);
    
    // Update local store
    const existing = await this.syncService.getLocal('inspections', inspectionId);
    if (existing) {
      await this.syncService.saveLocal('inspections', { 
        ...existing, 
        ...submission.data,
        checklist_responses: completionData.checklist_responses || existing.checklist_responses
      }, 'update');
    }
    
    if (this.syncService.isOnline) {
      await this.syncFormSubmissions();
    }

    return submission;
  }

  /**
   * Queue a new inspection creation for offline submission
   */
  async queueInspectionCreate(inspectionData) {
    const id = `insp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const submission = {
      id,
      type: 'inspection',
      action: 'create',
      data: {
        ...inspectionData,
        id,
        status: 'scheduled',
        created_at: new Date().toISOString(),
        offline_created: true
      },
      timestamp: new Date().toISOString(),
      synced: false,
      retryCount: 0
    };

    await this.saveFormSubmission(submission);
    
    await this.syncService.saveLocal('inspections', submission.data, 'create');
    
    if (this.syncService.isOnline) {
      await this.syncFormSubmissions();
    }

    return submission;
  }

  /**
   * Queue an asset creation for offline submission
   */
  async queueAssetCreate(assetData) {
    const id = `asset_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    const submission = {
      id,
      type: 'asset',
      action: 'create',
      data: {
        ...assetData,
        id,
        status: 'active',
        created_at: new Date().toISOString(),
        offline_created: true
      },
      timestamp: new Date().toISOString(),
      synced: false,
      retryCount: 0
    };

    await this.saveFormSubmission(submission);
    
    await this.syncService.saveLocal('assets', submission.data, 'create');
    
    if (this.syncService.isOnline) {
      await this.syncFormSubmissions();
    }

    return submission;
  }

  /**
   * Save form submission to IndexedDB
   */
  async saveFormSubmission(submission) {
    if (!this.syncService.db) await this.syncService.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.syncService.db.transaction('form_submissions', 'readwrite');
      const store = transaction.objectStore('form_submissions');
      
      store.put(submission);
      
      transaction.oncomplete = () => {
        this.syncService.notifyListeners({ 
          type: 'form_queued', 
          submission 
        });
        resolve(submission);
      };
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get pending form submissions
   */
  async getPendingSubmissions() {
    if (!this.syncService.db) await this.syncService.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.syncService.db.transaction('form_submissions', 'readonly');
      const store = transaction.objectStore('form_submissions');
      const request = store.getAll();
      
      request.onsuccess = () => {
        const results = (request.result || []).filter(item => !item.synced);
        resolve(results);
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Sync all pending form submissions
   */
  async syncFormSubmissions() {
    if (!this.syncService.isOnline) return { synced: 0, failed: 0 };
    
    const pending = await this.getPendingSubmissions();
    if (pending.length === 0) return { synced: 0, failed: 0 };
    
    let synced = 0;
    let failed = 0;
    
    const token = localStorage.getItem('token');
    
    for (const submission of pending) {
      try {
        let response;
        const baseUrl = process.env.REACT_APP_BACKEND_URL;
        
        if (submission.action === 'create') {
          // Create new entity
          const endpoint = {
            'work_order': '/api/work-orders',
            'inspection': '/api/inspections',
            'asset': '/api/assets'
          }[submission.type];
          
          if (!endpoint) continue;
          
          response = await fetch(`${baseUrl}${endpoint}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(submission.data)
          });
        } else if (submission.action === 'update' || submission.action === 'complete') {
          // Update existing entity
          const endpoint = {
            'work_order': `/api/work-orders/${submission.entityId}`,
            'inspection': `/api/inspections/${submission.entityId}`,
            'asset': `/api/assets/${submission.entityId}`
          }[submission.type];
          
          if (!endpoint) continue;
          
          response = await fetch(`${baseUrl}${endpoint}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(submission.data)
          });
        }
        
        if (response && response.ok) {
          await this.markSubmissionSynced(submission.id);
          synced++;
          this.syncService.notifyListeners({ 
            type: 'form_synced', 
            submission 
          });
        } else {
          // Increment retry count
          submission.retryCount = (submission.retryCount || 0) + 1;
          submission.lastError = response ? `HTTP ${response.status}` : 'Network error';
          await this.saveFormSubmission(submission);
          failed++;
          
          if (submission.retryCount >= 3) {
            this.syncService.notifyListeners({ 
              type: 'form_failed', 
              submission,
              error: 'Max retries exceeded'
            });
          }
        }
      } catch (error) {
        console.error('Form submission sync error:', error);
        submission.retryCount = (submission.retryCount || 0) + 1;
        submission.lastError = error.message;
        await this.saveFormSubmission(submission);
        failed++;
      }
    }
    
    this.syncService.notifyListeners({ 
      type: 'form_sync_complete', 
      synced, 
      failed 
    });
    
    return { synced, failed };
  }

  /**
   * Mark a submission as synced
   */
  async markSubmissionSynced(id) {
    if (!this.syncService.db) await this.syncService.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.syncService.db.transaction('form_submissions', 'readwrite');
      const store = transaction.objectStore('form_submissions');
      
      const request = store.get(id);
      request.onsuccess = () => {
        if (request.result) {
          const item = request.result;
          item.synced = true;
          item.syncedAt = new Date().toISOString();
          store.put(item);
        }
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  /**
   * Delete a pending submission (cancel)
   */
  async cancelSubmission(id) {
    if (!this.syncService.db) await this.syncService.init();
    
    return new Promise((resolve, reject) => {
      const transaction = this.syncService.db.transaction('form_submissions', 'readwrite');
      const store = transaction.objectStore('form_submissions');
      
      store.delete(id);
      
      transaction.oncomplete = () => resolve(true);
      transaction.onerror = () => reject(transaction.error);
    });
  }

  /**
   * Get sync status for forms
   */
  async getFormSyncStatus() {
    const pending = await this.getPendingSubmissions();
    
    return {
      pending: pending.length,
      byType: {
        work_orders: pending.filter(p => p.type === 'work_order').length,
        inspections: pending.filter(p => p.type === 'inspection').length,
        assets: pending.filter(p => p.type === 'asset').length
      },
      failedSubmissions: pending.filter(p => (p.retryCount || 0) >= 3).length
    };
  }
}

// Export singleton instance of form service
export const offlineFormService = new OfflineFormService();
